import { LineClose } from '@qr-front/icons/line-close'
import { LineSettingsAdjustmentHorizontal } from '@qr-front/icons/line-settings-adjustment-horizontal'
import { useState } from 'react'

import { cn, getVersionForClone } from '@/utils'

import { Button } from '../../../components/Button'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetTrigger,
} from '../../../components/Sheet'
import { ChipsFilter } from './ChipsFilter'
import { TFilterConfig } from './FiltersSheetTypes'
import { RadioFilter } from './RadioFilter'

export type TFiltersSheetProps = {
	filters: TFilterConfig[]
	onClearAll?: () => void
	totalActiveFilters?: number
	translations: {
		title: string
		clearAllButton: string
		seeResultsButton: string
	}
	className?: string
}

export const FiltersSheet = ({
	filters,
	onClearAll,
	totalActiveFilters = 0,
	translations,
	className,
}: TFiltersSheetProps) => {
	const [isOpen, setIsOpen] = useState(false)

	const handleClearAll = () => {
		onClearAll?.()
		setIsOpen(false)
	}

	const handleSeeResults = () => {
		setIsOpen(false)
	}

	return (
		<div className={cn('flex', className)}>
			<Sheet open={isOpen} onOpenChange={setIsOpen}>
				<SheetTrigger asChild>
					<Button
						visual="soft"
						size="icon"
						className={cn(
							'relative h-10 w-10',
							getVersionForClone({ base: '', clone1: 'rounded-8' }),
						)}
						onClick={() => setIsOpen(true)}
					>
						<LineSettingsAdjustmentHorizontal className="h-6 w-6" />
						{totalActiveFilters > 0 && (
							<div className="type-10 absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-blue p-0.5 font-medium leading-[0.625rem] text-white">
								{totalActiveFilters}
							</div>
						)}
					</Button>
				</SheetTrigger>
				<SheetContent
					side="bottom"
					className={cn(
						'h-auto max-h-[calc(100%-76px)] w-full rounded-t-10 border-none',
						'p-0 mobile:p-0 tablet:p-0',
						'bg-white',
						'data-[state=open]:slide-in-from-bottom',
						'data-[state=closed]:slide-out-to-bottom',
					)}
				>
					<SheetHeader className="flex items-center justify-between border-b border-border-grey-50 px-5 py-4">
						<h2 className="type-heading-4 text-dark-grey">
							{translations.title}
						</h2>
						<SheetClose asChild>
							<button className="flex items-center justify-center">
								<LineClose className="h-4 w-4 text-dark-grey" />
							</button>
						</SheetClose>
					</SheetHeader>
					<div className="flex flex-col overflow-y-auto">
						<div className="flex flex-col gap-4 px-5 py-4">
							{filters.map((filter, index) => {
								if (filter.type === 'radio') {
									return (
										<RadioFilter
											key={index}
											title={filter.title}
											options={filter.options}
											selectedValue={filter.selectedValue}
											onChange={filter.onChange}
										/>
									)
								}

								return (
									<ChipsFilter
										key={index}
										title={filter.title}
										options={filter.options}
										selectedValues={filter.selectedValues}
										onChange={filter.onChange}
									/>
								)
							})}
						</div>
					</div>
					<div className="flex gap-4 px-5 py-4 shadow-generator">
						<Button
							visual="soft"
							size="default"
							className="flex-1 text-blue ring-blue"
							onClick={handleClearAll}
						>
							{translations.clearAllButton}
						</Button>
						<Button
							visual="default"
							size="default"
							className="flex-1"
							onClick={handleSeeResults}
						>
							{translations.seeResultsButton}
						</Button>
					</div>
				</SheetContent>
			</Sheet>
		</div>
	)
}
